import { useState } from "react";
import {
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Table,
  Paper,
  TableContainer,
  AccordionDetails,
  CardContent,
  useMediaQuery,
  Button,
  Snackbar,
  Alert,
  Box,
  Grid,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import translations from "../translations/orderDetailsTranslation";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";
import serviceFile from "../Utils/serviceFile";

const OrderDetails = ({ sp, product_columns, onEditOrder, onDuplicateOrder }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isTracking, setIsTracking] = useState(false);
  const [trackingStatus, setTrackingStatus] = useState(null);
  const [isCheckingEdit, setIsCheckingEdit] = useState(false);
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const downloadFile = async (file) => {
    try {
      const resp = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/downloadFile`,
        file
      );
      serviceFile.downloadFile(resp, file.filename);
    } catch (err) {
      setSnackbar({
        open: true,
        message: translations.downloadFileErr,
        severity: "error",
      });
    }
  };

  const trackOrder = async () => {
    console.log("שולח מעקב להזמנה מספר:", sp.ordName);
    setIsTracking(true);
    try {
      const resp = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/trackOrder/${sp.ordName}`
      );
      console.log("Track Order Response:", resp);
      // לוקחים את הפריט האחרון מהמערך ואת הסטטוס שלו
      const lastStatus = resp?.data && resp.data.length > 0 ? resp.data[resp.data.length - 1].status : null;
      setTrackingStatus(lastStatus);
    } catch (err) {
      console.error("Track Order Error:", err);
      setSnackbar({
        open: true,
        message: "שגיאה במעקב הזמנה",
        severity: "error",
      });
    } finally {
      setIsTracking(false);
    }
  };

  const checkAndEditOrder = async () => {
    console.log("בודק אפשרות עריכה להזמנה מספר:", sp.ordName);
    setIsCheckingEdit(true);
    try {
      const resp = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/trackOrder/${sp.ordName}`
      );
      console.log("Track Order Response for Edit:", resp);

      // בדיקה אם יש סטטוס שמכיל את המילה "ליקוט"
      const hasLikut = resp?.data && resp.data.some(item => item.status.includes("ליקוט"));

      if (hasLikut) {
        setSnackbar({
          open: true,
          message: "לא ניתן לערוך הזמנה שכבר החלה בליקוט",
          severity: "warning",
        });
      } else {
        // אם אין ליקוט, אפשר לערוך
        if (onEditOrder) {
          onEditOrder(sp);
        }
      }
    } catch (err) {
      console.error("Error checking edit permission:", err);
      setSnackbar({
        open: true,
        message: "שגיאה בבדיקת אפשרות עריכה",
        severity: "error",
      });
    } finally {
      setIsCheckingEdit(false);
    }
  };

  return (
    <div>
      <AccordionDetails>
        <Box sx={{ bgcolor: "#ebf7ff", p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body1" sx={{ fontWeight: "normal" }}>
                {translations.orderNumber}:{" "}
                <span style={{ fontWeight: "bold" }}>{sp.ordName}</span>
              </Typography>
              <Typography variant="body1">
                {translations.status}:{" "}
                <span style={{ fontWeight: "bold" }}>
                  {trackingStatus || sp.status}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body1">
                {translations.orderDate}:{" "}
                <span style={{ fontWeight: "bold" }}>
                  {sp.createdAt.split("T")[0]}
                </span>
              </Typography>
              <Typography variant="body1">
                {translations.deliveryDate}:{" "}
                <span style={{ fontWeight: "bold" }}>{sp.deliveryDate}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body1">
                {translations.customerName}:{" "}
                <span style={{ fontWeight: "bold" }}>
                  {sp.selectedOrderingCustomer}
                </span>
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={trackOrder}
                  disabled={isTracking}
                  sx={{
                    color: "#0054A5",
                    textTransform: "none",
                    minWidth: "auto",
                    fontWeight: "bold",
                    fontStyle: "italic",
                  }}
                >
                  {isTracking ? "טוען..." : "סטטוס משלוח"}
                </Button>
                {onEditOrder && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={checkAndEditOrder}
                    disabled={isCheckingEdit}
                    sx={{
                      color: "#ed6c02",
                      textTransform: "none",
                      minWidth: "auto",
                      fontWeight: "bold",
                      fontStyle: "italic",
                    }}
                  >
                    {isCheckingEdit ? "בודק..." : "ערוך הזמנה"}
                  </Button>
                )}
                {onDuplicateOrder && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onDuplicateOrder(sp)}
                    sx={{
                      color: "#2e7d32",
                      textTransform: "none",
                      minWidth: "auto",
                      fontWeight: "bold",
                      fontStyle: "italic",
                    }}
                  >
                    {translations.duplicateOrder}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: isDesktop ? 2 : 1 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#0054A5", mb: 2 }}
          >
            {translations.productsInOrder}
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ mb: 4, bgcolor: "transparent" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  {product_columns.map((column, index) => (
                    <TableCell
                      key={index}
                      style={{
                        minWidth: column.minWidth,
                        fontWeight: "bold",
                        color: "#0054A5",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sp.selectedProducts.map((p, idx) => (
                  <Tooltip
                    key={idx}
                    title={
                      p.isOverLimit ? translations.productQuantityOverLimit : ""
                    }
                    enterDelay={100}
                  >
                    <TableRow
                      key={idx}
                      hover
                      sx={{ backgroundColor: p.isOverLimit ? "#FFF3E0" : null }}
                    >
                      {product_columns.map((column, index) => (
                        <TableCell key={index}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {typeof p[column.id] === "object" &&
                            p[column.id] !== null
                              ? p[column.id].address
                              : p[column.id]}
                            {p.orderApprovalRequired && index === 0 && (
                              <Tooltip
                                title={translations.productRequiresApproval}
                              >
                                <span>
                                  <InfoIcon
                                    color="warning"
                                    sx={{
                                      fontSize: 20,
                                      opacity: 0.8,
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            )}
                            {p.isOverLimit && index === 0 && (
                              <Tooltip
                                title={translations.productRequiresApproval}
                              >
                                <span></span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </Tooltip>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{ p: 2, bgcolor: "#f8f8f8", height: "100%" }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: "#0054A5" }}>
                  {translations.driverNotes}
                </Typography>
                <Typography>
                  {sp.drivertext || translations.noDriverNotes}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{ p: 2, bgcolor: "#f8f8f8", height: "100%" }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: "#0054A5" }}>
                  {translations.generalText}
                </Typography>
                <Typography>
                  {sp.orderstext || translations.noGeneralText}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8f8f8" }}>
                <Typography variant="h6" gutterBottom sx={{ color: "#0054A5" }}>
                  {translations.attachedFiles}
                </Typography>
                <Grid container spacing={1}>
                  {sp.selectedFiles.length ? (
                    sp.selectedFiles.map((file, index) => (
                      <Grid item key={index}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FileDownloadIcon />}
                          onClick={() =>
                            downloadFile({
                              filename: file.filename,
                              path: file.file_path,
                              filetype: file.type,
                              sysenv: process.env.REACT_APP_PYTHON_GETFILES,
                              filesize: file.size,
                            })
                          }
                          sx={{
                            borderColor: "#0054A5",
                            color: "#0054A5",
                            "&:hover": {
                              backgroundColor: "rgba(0, 84, 165, 0.04)",
                              borderColor: "#0054A5",
                            },
                          }}
                        >
                          {file.filename}
                        </Button>
                      </Grid>
                    ))
                  ) : (
                    <Grid item>
                      <Typography>{translations.noAttachedFiles}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </AccordionDetails>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OrderDetails;
